import {
  MouseEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import { BrowserRouter } from "react-router-dom";

import { differenceInSeconds } from "date-fns";

import { PageLoader } from "src/components/pageLoader";
import { VersionUpdateBanner } from "src/components/versionUpdateBanner/versionUpdateBanner";
import { ThemeContext } from "src/core/themeContext";
import { Fred } from "src/gen/rest/fred/FredService";
import { ErrCode } from "src/gen/schema/common/error";
import { GetLimitsResponse } from "src/gen/schema/configd/api";
import { GetUserContextsResponse } from "src/gen/schema/fred/fapi";
import { UserPreferences } from "src/gen/schema/models/user";
import { ToastProvider, useToast } from "src/hooks/useToasts";
import { useVersionUpdate } from "src/hooks/useVersionUpdate";
import { clearActiveApiOrigin } from "src/utils/activeApiEndpoint";
import { ConfigD } from "src/utils/api/configD";
import { Tenants } from "src/utils/api/rest/tenants";
import { BILGE_ID } from "src/utils/api/streaming/bilge";
import { Constants } from "src/utils/constants";
import {
  getServerTimeNow,
  getSessionAbortController,
  getUserSessionWithServerTime,
  UserSessionWithServerTime
} from "src/utils/session";
import { getLocalTheme, setLocalTheme } from "src/utils/theme";

import { MessageManager } from "./streaming/core/messageManager";
import { isApiError } from "./apiError";
import GlobalStore from "./appState";
import {
  IDLE_TIMEOUT,
  isEmail,
  MIN_REFRESH_INTERVAL,
  noop,
  PATHS,
  REFRESH_WINDOW
} from "./constants";
import { logger } from "./globals";
import { LoginContext } from "./loginContext";
import { UIRoutes } from "./routes";
import { Shell } from "./shell";

import "./app.scss";

/**
 * Component that checks for version update success and shows a toast.
 * Must be used within ToastProvider.
 */
const VersionUpdateSuccessToast = ({
  updatedVersion,
  onClear
}: {
  readonly updatedVersion: string | null;
  readonly onClear: () => void;
}): null => {
  const { addToast } = useToast();

  useEffect(() => {
    if (updatedVersion) {
      onClear();
      const isDevMode = window.cnc?.settings?.get("dev");
      const message =
        `Nexus Hyperfabric is now running the latest version.` +
        (isDevMode ? ` (${updatedVersion})` : "");
      addToast({
        title: "Application update complete",
        message,
        status: "positive",
        duration: 5000
      });
    }
  }, [updatedVersion, onClear, addToast]);

  return null;
};

/**
 * Component that shows a toast when a version update fails.
 * Must be used within ToastProvider.
 */
const VersionUpdateErrorToast = ({
  error,
  onClear
}: {
  readonly error: string | null;
  readonly onClear: () => void;
}): null => {
  const { addToast } = useToast();

  useEffect(() => {
    if (error) {
      onClear();
      addToast({
        title: "Update failed",
        message: error,
        status: "warning",
        duration: 5000
      });
    }
  }, [error, onClear, addToast]);

  return null;
};

const { SESSION_LOGOUT } = Constants;

const GEO_MISMATCH_EMAIL_FIELD = "email";

const switchTenant = (id?: string) => {
  if (id) {
    Tenants.switchTenant(id, "/fabrics");
  }
};

const getGeoMismatchEmail = (error: unknown): string | undefined => {
  // Promise rejections are untyped; only the normalized REST ApiError
  // carries the geo-mismatch code and email field from Fred.
  if (
    !isApiError(error) ||
    error.getRestErrorCode() !== ErrCode.ERR_CODE_PROXY_GEO_MISMATCH
  ) {
    return undefined;
  }
  const email = error.getFieldErrors().get(GEO_MISMATCH_EMAIL_FIELD)?.value;
  return email && isEmail(email) ? email : undefined;
};

const refresh = (): Promise<boolean> => {
  return Fred.userSessionRefresh()
    .then((resp: Response) => {
      return resp.status === 200;
    })
    .catch((err: Error) => {
      logger.error("Refresh Session Exception:", err);
      return false;
    });
};

export default function App() {
  const [loading, setLoading] = useState<boolean>(true);
  const [session, setSession] = useState<UserSessionWithServerTime>();
  const [userContexts, setUserContexts] = useState<GetUserContextsResponse>();
  const form = useRef<HTMLFormElement>(null);
  const lastRefresh = useRef<number>(0);
  const expiry = session?.expiry;
  const serverClockSkewMs = session?.serverClockSkewMs;
  const { applyTheme } = useContext(ThemeContext);
  /* Org's configured limits */
  const [configLimits, setConfigLimits] = useState<GetLimitsResponse>();

  const {
    showBanner,
    mismatch,
    error: versionError,
    handleAccept: handleAcceptVersionUpdate,
    handleDismiss: handleDismissVersionUpdate,
    clearError: clearVersionError,
    checkVersion,
    updatedVersion,
    clearUpdatedVersion
  } = useVersionUpdate();

  const logout = useCallback((e?: MouseEvent<unknown>) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    clearActiveApiOrigin();
    setSession(undefined);
    setConfigLimits(undefined);
    form.current?.submit();
  }, []);

  const getUserPreferences = useCallback((): Promise<boolean> => {
    return Fred.getUserPreferences()
      .then((resp: UserPreferences) => {
        applyTheme(resp.theme);
        return true;
      })
      .catch((err) => {
        logger.error("Get User Preferences Error: ", err);
        return false;
      });
  }, [applyTheme]);

  /**
   * Fetches config limits from the server with automatic retries on failure.
   * Uses a recursive Promise chain to avoid async/await-in-loop lint violations.
   * Waits 2 seconds between each retry attempt.
   *
   * @param retries - Maximum number of retry attempts after the initial request.
   *                  Total attempts = retries + 1.
   * @returns A Promise that resolves when config limits are successfully fetched
   *          and stored, or rejects after all retry attempts are exhausted.
   */
  const getConfigLimitsWithRetry = useCallback(
    (retries: number): Promise<void> => {
      /**
       * Recursive inner function that performs a single attempt and schedules
       * the next retry on failure if the retry budget has not been exhausted.
       *
       * @param count - The current attempt index (0-based).
       */
      const attempt = (count: number): Promise<void> => {
        return ConfigD.getConfigLimits()
          .then((resp: GetLimitsResponse) => {
            setConfigLimits(resp);
          })
          .catch((err) => {
            logger.error(`getConfigLimits Failed ${count + 1}`, err);
            if (count < retries) {
              return new Promise<void>((res) => setTimeout(res, 2000)).then(
                () => attempt(count + 1)
              );
            }
            return Promise.reject();
          });
      };
      return attempt(0);
    },
    []
  );

  const getUserSession = useCallback(
    (isInitialLogin = false): Promise<boolean> => {
      return getUserSessionWithServerTime()
        .then((resp2: UserSessionWithServerTime) => {
          // Check for version mismatch — may redirect (returns true) or show banner
          return checkVersion(resp2.uiVersion, isInitialLogin).then(
            (didRedirect) => {
              if (didRedirect) return false;

              /**
               * if getUserSession succeeds then
               * the user has a valid cookie and
               * will go to the landing page.
               */
              setSession(resp2);
              // Always apply the locally-stored theme so the welcome screen and
              // any pre-preferences UI renders with the correct theme. The full
              // getUserPreferences fetch is only valid for existing users.
              const localTheme = getLocalTheme();
              if (localTheme !== undefined) {
                setLocalTheme(localTheme);
              }
              // Fetch contexts for both existing and signup users so the
              // scratch-org region picker can filter geos where the user
              // already has orgs.
              if (resp2) {
                Fred.getUserContexts().then(
                  (resp: GetUserContextsResponse) => {
                    setUserContexts(resp);
                  },
                  (err) => {
                    logger.error("Get User Contexts Error: ", err);
                  }
                );
              }

              // User preferences and config limits require an existing org
              // session. Signup users get those only after org creation.
              if (resp2 && !resp2.newUser) {
                getUserPreferences();

                /**
                 * there is an unhandled promise rejection
                 * when all retries are exhausted but even
                 * retrying is probably unnecessary.
                 */
                getConfigLimitsWithRetry(3);
              }
              return true;
            }
          );
        })
        .catch((err: unknown) => {
          const geoMismatchEmail = getGeoMismatchEmail(err);
          if (geoMismatchEmail) {
            // Fred has already cleared the wrong-geo session cookie. Send the
            // user to the local login route; LoginView will auto-submit when
            // the email query parameter is present and valid.
            const startUrl = window.location.href;
            const loginParams = new URLSearchParams({
              email: geoMismatchEmail,
              startUrl
            });
            getSessionAbortController().abort();
            clearActiveApiOrigin();
            window.location.assign(`/${PATHS.LOGIN}?${loginParams.toString()}`);
          }
          return false;
        });
    },
    [getConfigLimitsWithRetry, getUserPreferences, checkVersion]
  );

  /**
   * Initialize the App
   */
  useEffect(() => {
    setLoading(true);

    if (!window.cnc) {
      window.cnc = new GlobalStore();
    }

    getUserSession(true).then(() => {
      setLoading(false);
    });

    logger.setLevel(0); // probably change later for prod
  }, [getUserSession]);

  useEffect(() => {
    if (session?.orgId) {
      /**
       * Added so we are not trying to
       * connect to the websocket until
       * we have logged in. Otherwise, it
       * will cause error and keep trying
       * to connect. This will manager the
       * org level streaming.
       */
      const bilge = window.cnc.bilgePool.useStream(BILGE_ID);
      if (bilge) {
        bilge.init();
        return () => bilge.close();
      }
    }
    return MessageManager.unsubscribe;
  }, [session?.orgId]);

  /**
   * Initialize the Idle timeout &
   * refresh handler code.
   */
  useEffect(() => {
    if (expiry) {
      let lastUserActivity = Date.now();
      const sessionCheck = () => {
        const now = Date.now();
        /**
         * TODO: Adjust timeout to actual requirement at some point
         * If there has not been any activity within the
         * last 30 minutes, the user is idle and will be logged
         * out of the system
         */
        if (differenceInSeconds(now, lastUserActivity) > IDLE_TIMEOUT) {
          logout();
        }
        if (expiry) {
          const serverNow = getServerTimeNow(serverClockSkewMs);
          /**
           * If the user has not hit the idle timed out,
           * and is within the 5 minutes of the expiry
           * time of the session. The UI will refresh
           * the token to keep it alive.
           *
           * If for any reason the differenceInSeconds(expiry, serverNow) results
           * in true continuously, the second condition will prevent multiple
           * refresh calls in a short period of time.
           *
           * lastRefresh is set to epoch on page load so that the token will be refreshed
           * ASAP if within the refresh window.
           * This is to address the scenario user loading the page with an almost expired token
           * and not getting the token refreshed until after the minimum refresh interval.
           */
          if (
            differenceInSeconds(now, lastRefresh.current) >=
              MIN_REFRESH_INTERVAL &&
            differenceInSeconds(expiry, serverNow) < REFRESH_WINDOW
          ) {
            lastRefresh.current = now;
            refresh().then((success: boolean) => {
              if (success) {
                getUserSession(false);
              }
            });
          }
        }
      };
      const id: NodeJS.Timeout = setInterval(sessionCheck, 30000);
      /**
       * TORT-3467 WebUI: token refresh timer gets reset upon page load
       * Added to address bug automation
       * testing introduced by refreshing
       * the browser every 5 seconds.
       */
      sessionCheck();

      const logoutHandler = () => logout();
      const sessionAbortController = getSessionAbortController();
      document.body.addEventListener(SESSION_LOGOUT.type, logoutHandler, {
        signal: sessionAbortController.signal
      });

      const body = document.body;
      /**
       * Event handlers for body.onclick, body.oncontextmenu, &
       * body.onmousemove all use the same function pointer to
       * update the Sessions idleSinceMilliseconds. This will
       * be used to drive idle logout.
       */
      body.onclick =
        body.oncontextmenu =
        body.onmousemove =
          () => {
            lastUserActivity = Date.now();
          };
      /**
       * Prevent the default behavior
       * of the dragover & drop event
       * so files are not saved by the
       * browser.
       */
      body.ondrop = body.ondragover = (e) => {
        e.preventDefault();
        e.stopPropagation();
      };

      return () => {
        clearInterval(id);
        /**
         * Clear all the body event
         * listeners
         */
        sessionAbortController.abort();

        body.onclick =
          body.oncontextmenu =
          body.onmousemove =
          body.ondrop =
          body.ondragover =
            noop;
      };
    }
    return noop;
  }, [getUserSession, expiry, logout, serverClockSkewMs]);

  if (loading) {
    return <PageLoader />;
  }

  return (
    <ToastProvider>
      <LoginContext.Provider
        value={{
          configLimits,
          session,
          getUserSession,
          logout,
          switchTenant,
          userContexts,
          setUserContexts
        }}
      >
        <VersionUpdateSuccessToast
          updatedVersion={updatedVersion}
          onClear={clearUpdatedVersion}
        />
        <VersionUpdateErrorToast
          error={versionError}
          onClear={clearVersionError}
        />
        <VersionUpdateBanner
          show={!!(showBanner && mismatch)}
          currentVersion={__APP_VERSION__}
          serverVersion={mismatch?.serverVersion ?? ""}
          onAccept={handleAcceptVersionUpdate}
          onDismiss={handleDismissVersionUpdate}
        />
        <div className="hyperfabric">
          <form
            ref={form}
            method="post"
            action={
              session?.isC3Crosslaunch
                ? "/c3/v1/sessions/logout"
                : "/ui/v1/sessions/logout"
            }
          >
            {!session?.isC3Crosslaunch && (
              <input
                type="hidden"
                name="startUrl"
                value={`${window.location.origin}${window.location.pathname}`}
              />
            )}
          </form>
          <BrowserRouter>
            <Shell>
              {/*Commenting out for time being, to prevent scroll to the top */}
              {/*<ScrollToTop />*/}
              <UIRoutes />
            </Shell>
          </BrowserRouter>
        </div>
      </LoginContext.Provider>
    </ToastProvider>
  );
}
